import { createSelector } from '@ngrx/store';
import { AppState } from '../app.state';
import {
	portfolioItemsForExcelExportFields,
	PortfolioItemsWithCalculation
} from '@easy-lib/modules/portfolio/models/portfolio-item.model';
import { IPortfolioItemsCalculationTotalFields } from '@easy-lib/modules/portfolio';

const getPortfolioState = (state: AppState) => state.portfolioState;
const getPortfolioMarketDataState = (state: AppState) => state.portfolioMarketDatastate.entities;

export const getPortfolioErrorState = createSelector(getPortfolioState, state => state?.error);
export const getPortfolioLoading = createSelector(getPortfolioState, state => state?.loading);
export const getLastPortfolioInsertTime = createSelector(getPortfolioState, state => state?.lastPortfolioInsertTime);
export const getPortfolioItems = createSelector(getPortfolioState, state =>
	Object.values(state.entities)
		.filter(a => a.asset + a.bonusShareVolume > 0)
		.map(item => new PortfolioItemsWithCalculation({ ...item }))
);
export const getPortfolioMarketData = createSelector(getPortfolioMarketDataState, state => Object.values(state));

export const getPortfolioItem = (symbolIsin: string) =>
	createSelector(getPortfolioState, state => state.entities[symbolIsin]);

export const getPortfolioItemMarketData = (symbolIsin: string) =>
	createSelector(getPortfolioMarketDataState, state => state[symbolIsin]);

export const getPortfolioItemWithCalculation = (symbolIsin: string) =>
	createSelector(getPortfolioItem(symbolIsin), getPortfolioItemMarketData(symbolIsin), (pItem, marketData) => {
		return new PortfolioItemsWithCalculation({ ...pItem, ...marketData });
	});

export const getPortfolioAllItems = createSelector(
	getPortfolioItems,
	getPortfolioMarketDataState,
	(portfolioItems, marketData) => {
		const newPortfolioItems = portfolioItems.map(x => {
			const specificMarketData = marketData[x.symbolIsin] ?? {};
			return new PortfolioItemsWithCalculation({ ...x, ...specificMarketData });
		});
		const _totalPortfolioValue = newPortfolioItems
			.map(x => x.currentValue + x.currentBonusShareValue)
			.reduce((prev, current) => prev + current, 0);
		newPortfolioItems.forEach(item => {
			item.portfolioPercent =
				Math.round(((item.currentValue + item.currentBonusShareValue) / _totalPortfolioValue) * 10000) / 100;
			return item;
		});
		return newPortfolioItems;
	}
);

export const getPortfolioDPSListItems = createSelector(getPortfolioAllItems, portfolioItems => {
	return portfolioItems
		.filter(item => item.totalDPSAmount > 0 || item.lastDPSAmount > 0)
		.toSort((a, b) => b.lastDPSAmount - a.lastDPSAmount)
		.map(item => new PortfolioItemsWithCalculation({ ...item }));
});

export const getPortfolioItemsForExcel = createSelector(getPortfolioAllItems, portfolioItems => {
	const headers = portfolioItemsForExcelExportFields.map(x => ({
		title: x.farsiTitle
	}));
	const values = portfolioItems
		.map(x => {
			return new PortfolioItemsWithCalculation({ ...x });
		})
		.map(data => {
			const resultData = {};
			portfolioItemsForExcelExportFields.forEach(info => {
				resultData[info.fieldName] = data[info.fieldName];
			});
			return resultData;
		});

	return { headers, values };
});

export const getPortfolioItemTotalValues = createSelector(getPortfolioAllItems, portfolioItems => {
	const _totalProfitAmount = portfolioItems.map(x => x.profitAmount).reduce((prev, current) => +prev + +current, 0);
	const _totalPortfolioValue = portfolioItems.map(x => x.currentValue).reduce((prev, current) => prev + current, 0);
	const _totalYesterdayPortfolioValue = portfolioItems
		.map(x => x.yesterdayValue)
		.reduce((prev, current) => +prev + +current, 0);
	const _totalBonusShareValue = portfolioItems
		.map(x => x.currentBonusShareValue)
		.reduce((prev, current) => +prev + +current, 0);

	let _totalAssetsValue = portfolioItems.map(x => x.currentValue).reduce((prev, current) => prev + current, 0);

	_totalAssetsValue += _totalBonusShareValue;

	const result: IPortfolioItemsCalculationTotalFields = {
		totalAssetsValue: _totalAssetsValue,
		totalAssetCount: portfolioItems.map(x => x.asset).reduce((prev, current) => prev + current, 0),
		totalPortfolioValue: _totalPortfolioValue,
		totalBonusValue: portfolioItems.map(x => x.currentBonusShareValue).reduce((prev, current) => prev + current, 0),
		totalProfitAmount: _totalProfitAmount,
		totalProfitPercent: (_totalProfitAmount / (_totalPortfolioValue + _totalBonusShareValue)) * 100 || 0,
		totalLastDPSAmount: portfolioItems
			.map(x => x.lastDPSAmount ?? 0)
			.reduce((prev, current) => +prev + +current, 0),
		totalTodayProfitAmount: portfolioItems.map(x => x.todayProfit).reduce((prev, current) => +prev + +current, 0),
		totalTodayProfitPercent:
			((_totalPortfolioValue + _totalBonusShareValue - _totalYesterdayPortfolioValue) /
				_totalYesterdayPortfolioValue) *
			100,
		totalSoldProfit: portfolioItems.map(x => x.soldProfit).reduce((prev, current) => +prev + +current, 0)
	};
	return result;
});
